import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem("aivos_user") || "null");
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(false);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("aivos_token", data.token);
        localStorage.setItem("aivos_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name, role: "owner" });
        localStorage.setItem("aivos_token", data.token);
        localStorage.setItem("aivos_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("aivos_token");
        localStorage.removeItem("aivos_user");
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const { data } = await api.get("/auth/me");
            // Merge with the token (status / is_admin / must_change_password
            // come from /auth/me; the token + email stay from login).
            setUser(prev => {
                const merged = { ...(prev || {}), ...data };
                localStorage.setItem("aivos_user", JSON.stringify(merged));
                return merged;
            });
            return data;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        refreshUser().finally(() => setLoading(false));
        // eslint-disable-next-line
    }, []);

    // Phase H — account-isolation hardening. If another tab logs in or out
    // on the same browser (localStorage is shared across tabs of the same
    // origin), the `storage` event fires. Sync this tab's React state so
    // there's NEVER a mismatch between the localStorage token and the
    // in-memory user object.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key !== "aivos_user" && e.key !== "aivos_token") return;
            if (!e.newValue) {
                // logged out elsewhere → wipe here too
                setUser(null);
                return;
            }
            if (e.key === "aivos_user") {
                try {
                    const nextUser = JSON.parse(e.newValue);
                    // different user id → reload to cleanly re-mount the
                    // whole app tree with the new identity; same user id →
                    // in-place update.
                    setUser(prev => {
                        if (prev && nextUser && prev.id && nextUser.id && prev.id !== nextUser.id) {
                            window.location.reload();
                            return prev;
                        }
                        return nextUser;
                    });
                } catch { /* noop */ }
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    return (
        <AuthCtx.Provider value={{ user, login, register, logout, loading, refreshUser }}>
            {children}
        </AuthCtx.Provider>
    );
}

export function useAuth() {
    return useContext(AuthCtx);
}
